import { BackendError, Service } from '@genial/services';
import { FinanceEnumsConsolidatedProductTypeEnum } from '@genial/services/dist/models';
import { selectorFamily } from 'recoil';

import { customersAssetsDateSelected } from '../customerDetail/atom';

import { customerAssetDetailsFilters } from './atom';
import { screenName } from './constants';
import {
  normalizeAssetsFilters,
  normalizeClubsAssets,
  normalizeCoeAssets,
  normalizeCryptoAssets,
  normalizeDirectTreasuryAssets,
  normalizeEquityAssets,
  normalizeFixedIncomeAssets,
  normalizeFundsAssets,
  normalizePrivatePensionAssets,
} from './normalize';
import {
  AbstractAssetsQueryData,
  AssetFiltersResponse,
  AssetsFilterProps,
  ClubsAssetsQueryItem,
  CoeAssetsQueryItem,
  CryptoAssetsQueryItem,
  CustomerAssetDetailsFiltersAtom,
  DirectTreasuryAssetsQueryItem,
  EquityAssetsQueryItem,
  FixedIncomeAssetsQueryItem,
  FundsAssetsQueryItem,
  PrivatePensionAssetsQueryItem,
} from './types';

export type FundsAssetsQueryData = AbstractAssetsQueryData<FundsAssetsQueryItem>;

export const fundsAssetsQuery = selectorFamily<
  | {
      data: FundsAssetsQueryData;
      totalInvestedValue: number;
    }
  | undefined,
  { accountNumber: number }
>({
  key: 'CustomerAssetDetail/fundsAssetsQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) => {
      try {
        const selectedDate = get(customersAssetsDateSelected);
        const categoryFilter = get(customerAssetDetailsFilterItemSelected({ productType: '1' }));

        const { data } = await Service.Finance.V1.getConsolidatedByFundsAccountNumber({
          screenName,
          accountNumber,
          positionDate: selectedDate,
          ...(categoryFilter ? { categoryFilter } : {}),
        });

        const normalizedData = normalizeFundsAssets(data.fundsProductPosition);

        return { data: normalizedData, totalInvestedValue: data.totalInvestedValue };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], totalInvestedValue: 0 };
        }

        throw error;
      }
    },
});

export type CryptoAssetsQueryData = AbstractAssetsQueryData<CryptoAssetsQueryItem>;

export const cryptoAssetsQuery = selectorFamily<
  | {
      data: CryptoAssetsQueryData;
    }
  | undefined,
  { accountNumber: number }
>({
  key: 'CustomerAssetDetail/cryptoAssetsQuery',
  get:
    ({ accountNumber }) =>
    async () => {
      try {
        const { data } = await Service.Finance.V1.getConsolidatedByCriptoAccountNumber({
          screenName,
          accountNumber,
        });

        const normalizedData = normalizeCryptoAssets(data.criptoProductPosition);

        return { data: normalizedData };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [] };
        }

        throw error;
      }
    },
});

export type FixedIncomeAssetsQueryData = AbstractAssetsQueryData<FixedIncomeAssetsQueryItem>;

export const fixedIncomeAssetsQuery = selectorFamily<
  | {
      data: FixedIncomeAssetsQueryData;
      totalInvestedValue: number;
    }
  | undefined,
  { accountNumber: number }
>({
  key: 'CustomerAssetDetail/fixedIncomeAssetsQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) => {
      try {
        const selectedDate = get(customersAssetsDateSelected);
        const categoryFilter = get(customerAssetDetailsFilterItemSelected({ productType: '3' }));

        const { data } = await Service.Finance.V1.getConsolidatedByFixedIncomeAccountNumber({
          screenName,
          accountNumber,
          positionDate: selectedDate,
          ...(categoryFilter ? { categoryFilter } : {}),
        });

        const normalizedData = normalizeFixedIncomeAssets(data.fixedIncomeProductPosition);

        return { data: normalizedData, totalInvestedValue: data.totalInvestedValue };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], totalInvestedValue: 0 };
        }

        throw error;
      }
    },
});

export type DirectTreasuryAssetsQueryData = AbstractAssetsQueryData<DirectTreasuryAssetsQueryItem>;

export const directTreasuryAssetsQuery = selectorFamily<
  | {
      data: DirectTreasuryAssetsQueryData;
      totalInvestedValue: number;
    }
  | undefined,
  { accountNumber: number }
>({
  key: 'CustomerAssetDetail/directTreasuryAssetsQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) => {
      try {
        const selectedDate = get(customersAssetsDateSelected);
        const categoryFilter = get(customerAssetDetailsFilterItemSelected({ productType: '6' }));

        const { data } = await Service.Finance.V1.getConsolidatedByDirectTreasuryAccountNumber({
          screenName,
          accountNumber,
          positionDate: selectedDate,
          ...(categoryFilter ? { categoryFilter } : {}),
        });

        const normalizedData = normalizeDirectTreasuryAssets(data.treasuryProductPosition);

        return { data: normalizedData, totalInvestedValue: data.totalInvestedValue };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], totalInvestedValue: 0 };
        }

        throw error;
      }
    },
});

export type EquityAssetsQueryData = AbstractAssetsQueryData<EquityAssetsQueryItem>;

export const equityAssetsQuery = selectorFamily<
  | {
      data: EquityAssetsQueryData;
      totalInvestedValue: number;
    }
  | undefined,
  { accountNumber: number }
>({
  key: 'CustomerAssetDetail/equityAssetsQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) => {
      try {
        const selectedDate = get(customersAssetsDateSelected);
        const categoryFilter = get(customerAssetDetailsFilterItemSelected({ productType: '4' }));

        const { data } = await Service.Finance.V1.getConsolidatedByEquityAccountNumber({
          screenName,
          accountNumber,
          positionDate: selectedDate,
          ...(categoryFilter ? { categoryFilter } : {}),
        });

        const normalizedData = normalizeEquityAssets(
          data.equityProductInCashPosition,
          data.equityProductTermPosition,
          data.equityProductOptionPosition,
          data.equityProductBmfPosition,
          data.recommendedPortfolios
        );

        return { data: normalizedData, totalInvestedValue: data.totalInvestedValue };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], totalInvestedValue: 0 };
        }

        throw error;
      }
    },
});

export type PrivatePensionAssetsQueryData = AbstractAssetsQueryData<PrivatePensionAssetsQueryItem>;

export const privatePensionAssetsQuery = selectorFamily<
  | {
      data: PrivatePensionAssetsQueryData;
      totalInvestedValue: number;
    }
  | undefined,
  { accountNumber: number }
>({
  key: 'CustomerAssetDetail/privatePensionAssetsQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) => {
      try {
        const selectedDate = get(customersAssetsDateSelected);
        const categoryFilter = get(customerAssetDetailsFilterItemSelected({ productType: '5' }));

        const { data } = await Service.Finance.V1.getConsolidatedByPrivatePensionAccountNumber({
          screenName,
          accountNumber,
          positionDate: selectedDate,
          ...(categoryFilter ? { categoryFilter } : {}),
        });

        const normalizedData = normalizePrivatePensionAssets(data.pensionProductPosition);

        return { data: normalizedData, totalInvestedValue: data.totalInvestedValue };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], totalInvestedValue: 0 };
        }

        throw error;
      }
    },
});

export type ClubsAssetsQueryData = AbstractAssetsQueryData<ClubsAssetsQueryItem>;

export const clubsAssetsQuery = selectorFamily<
  | {
      data: ClubsAssetsQueryData;
      totalInvestedValue: number;
    }
  | undefined,
  { accountNumber: number }
>({
  key: 'CustomerAssetDetail/clubsAssetsQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) => {
      try {
        const selectedDate = get(customersAssetsDateSelected);
        const categoryFilter = get(customerAssetDetailsFilterItemSelected({ productType: '2' }));

        const { data } = await Service.Finance.V1.getConsolidatedByClubsAccountNumber({
          screenName,
          accountNumber,
          positionDate: selectedDate,
          ...(categoryFilter ? { categoryFilter } : {}),
        });

        const normalizedData = normalizeClubsAssets(data.fundsProductPosition);

        return { data: normalizedData, totalInvestedValue: data.totalInvestedValue };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], totalInvestedValue: 0 };
        }

        throw error;
      }
    },
});

export type CoeAssetsQueryData = AbstractAssetsQueryData<CoeAssetsQueryItem>;

export const coeAssetsQuery = selectorFamily<
  | {
      data: CoeAssetsQueryData;
      totalInvestedValue: number;
    }
  | undefined,
  { accountNumber: number }
>({
  key: 'CustomerAssetDetail/coeAssetsQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) => {
      try {
        const selectedDate = get(customersAssetsDateSelected);
        const categoryFilter = get(customerAssetDetailsFilterItemSelected({ productType: '7' }));

        const { data } = await Service.Finance.V1.getConsolidatedByCoeAccountNumber({
          screenName,
          accountNumber,
          positionDate: selectedDate,
          ...(categoryFilter ? { categoryFilter } : {}),
        });

        const normalizedData = normalizeCoeAssets(data.coeProductPosition);

        return { data: normalizedData, totalInvestedValue: data.totalInvestedValue };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], totalInvestedValue: 0 };
        }

        throw error;
      }
    },
});

export const assetsFiltersGetter = async (
  productType: FinanceEnumsConsolidatedProductTypeEnum,
  accountNumber: number,
  selectedDate?: Date
) => {
  try {
    const { data } = await Service.Finance.V1.getConsolidatedByFiltersAccountNumber({
      screenName,
      accountNumber,
      consolidatedProductType: productType,
      positionDate: selectedDate,
    });

    const normalizedData = normalizeAssetsFilters(data);

    return { data: normalizedData };
  } catch (_error) {
    return { data: normalizeAssetsFilters([]) };
  }
};

export const emptyFiltersQuery = selectorFamily<AssetFiltersResponse, { accountNumber: number }>({
  key: 'CustomerAssetDetail/clubsFiltersQuery',
  get: () => async () => ({ data: [] }),
});

export const clubsFiltersQuery = selectorFamily<AssetFiltersResponse, { accountNumber: number }>({
  key: 'CustomerAssetDetail/clubsFiltersQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) =>
      await assetsFiltersGetter('CLUBS', accountNumber, get(customersAssetsDateSelected)),
});

export const coeFiltersQuery = selectorFamily<AssetFiltersResponse, { accountNumber: number }>({
  key: 'CustomerAssetDetail/coeFiltersQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) =>
      await assetsFiltersGetter('COE', accountNumber, get(customersAssetsDateSelected)),
});

export const directTreasuryFiltersQuery = selectorFamily<
  AssetFiltersResponse,
  { accountNumber: number }
>({
  key: 'CustomerAssetDetail/directTreasuryFiltersQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) =>
      await assetsFiltersGetter('DIRECT_TREASURY', accountNumber, get(customersAssetsDateSelected)),
});

export const equityFiltersQuery = selectorFamily<AssetFiltersResponse, { accountNumber: number }>({
  key: 'CustomerAssetDetail/equityFiltersQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) =>
      await assetsFiltersGetter('EQUITY', accountNumber, get(customersAssetsDateSelected)),
});

export const fixedIncomeFiltersQuery = selectorFamily<
  AssetFiltersResponse,
  { accountNumber: number }
>({
  key: 'CustomerAssetDetail/fixedIncomeFiltersQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) =>
      await assetsFiltersGetter('FIXED_INCOME', accountNumber, get(customersAssetsDateSelected)),
});

export const fundsFiltersQuery = selectorFamily<AssetFiltersResponse, { accountNumber: number }>({
  key: 'CustomerAssetDetail/fundsFiltersQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) =>
      await assetsFiltersGetter('FUNDS', accountNumber, get(customersAssetsDateSelected)),
});

export const privatePensionFiltersQuery = selectorFamily<
  AssetFiltersResponse,
  { accountNumber: number }
>({
  key: 'CustomerAssetDetail/privatePensionFiltersQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) =>
      await assetsFiltersGetter('PRIVATE_PENSION', accountNumber, get(customersAssetsDateSelected)),
});

export const updateCustomerAssetDetailsFilters = selectorFamily<
  AssetsFilterProps[],
  { productType: string }
>({
  key: 'updateCustomerAssetDetailsFilters',
  set:
    ({ productType }) =>
    ({ set, get }, newValue) => {
      const item = get(customerAssetDetailsFilters);
      return set(customerAssetDetailsFilters, {
        ...item,
        [productType]: {
          ...(item[productType] || {}),
          filters: newValue as AssetsFilterProps[],
        },
      });
    },
  get:
    ({ productType }) =>
    ({ get }) => {
      const item = get(customerAssetDetailsFilters);
      return item[productType]?.filters ?? [];
    },
});

export const updateCustomerAssetDetailsFiltersLoading = selectorFamily<
  boolean,
  { productType: string }
>({
  key: 'updateCustomerAssetDetailsFiltersLoading',
  set:
    ({ productType }) =>
    ({ set, get }, newValue) => {
      const item = get(customerAssetDetailsFilters);
      return set(customerAssetDetailsFilters, {
        ...item,
        [productType]: {
          ...(item[productType] || {}),
          loading: newValue,
        },
      } as CustomerAssetDetailsFiltersAtom);
    },
  get:
    ({ productType }) =>
    ({ get }) => {
      const item = get(customerAssetDetailsFilters);
      return item[productType]?.loading ?? false;
    },
});

export const updateCustomerAssetDetailsFilterSelected = selectorFamily<
  number,
  { productType: string }
>({
  key: 'updateCustomerAssetDetailsFilterSelected',
  set:
    ({ productType }) =>
    ({ set, get }, newValue) => {
      const item = get(customerAssetDetailsFilters);
      return set(customerAssetDetailsFilters, {
        ...item,
        [productType]: {
          ...(item[productType] || {}),
          selected: newValue,
        },
      } as CustomerAssetDetailsFiltersAtom);
    },
  get:
    ({ productType }) =>
    ({ get }) => {
      const item = get(customerAssetDetailsFilters);
      return item[productType]?.selected ?? 0;
    },
});

export const customerAssetDetailsFilterItemSelected = selectorFamily<
  string,
  { productType: string }
>({
  key: 'customerAssetDetailsFilterItemSelected',
  get:
    ({ productType }) =>
    ({ get }) => {
      const item = get(customerAssetDetailsFilters);
      const filters = item[productType]?.filters ?? [];
      const selected = item[productType]?.selected ?? 0;

      return filters?.[selected]?.value || '';
    },
});
